import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import {
  FeedFilterCombinator,
  FeedFilterGroup,
  FeedFilterTree,
  FeedFilters,
} from 'src/app/models/feed.model';
import {
  FEED_FILTER_FIELD_OPTIONS,
  FEED_FILTER_OPERATOR_OPTIONS,
  createEmptyFeedFilterGroup,
  createEmptyFeedFilterRule,
  getFeedFilterGroups,
  getFeedFilterTopOperator,
  isFeedFilterGroupValid,
  serializeFeedFilters,
} from 'src/app/models/feed-filter-rule.model';

// Formulário de filtros avançados de feed (SSAPP-5, grupos SSAPP-20).
// Diferente do app-rule-group (regras de URL de widget): contrato próprio
// (caption/hashtags/creator, contains/notContains), árvore de 2 níveis fixos
// (grupos -> regras, sem aninhamento arbitrário), e zero grupos é um estado
// VÁLIDO ("sem filtros"), não recriado automaticamente como o rule-group faz
// com condições de URL.
//
// Cada regra aceita só um valor (values[0]) — não é uma limitação do
// contrato (o backend aceita values: string[] com vários itens por regra,
// combinados com OU implícito em "contém" e E-das-negações em "não contém"),
// é decisão de UX: chips com múltiplos valores escondiam esse operador
// implícito. Combinar termos com E/OU fica explícito adicionando regras
// (dentro de um grupo) ou grupos (entre si), nunca digitando vários valores
// no mesmo campo.
//
// Lógica mista tipo "Termo A" E "Termo B" OU "Termo C" vira: grupo 1
// (operator 'and': Termo A, Termo B) + grupo 2 (Termo C sozinho), com
// topOperator 'or' combinando os dois grupos.
@Component({
  standalone: true,
  selector: 'app-feed-filter-builder',
  templateUrl: './feed-filter-builder.component.html',
  styleUrls: ['./feed-filter-builder.component.scss'],
  imports: [CommonModule, FormsModule, DropdownModule, InputTextModule, ButtonModule, SelectButtonModule],
})
export class FeedFilterBuilderComponent implements OnChanges {
  @Input() disabled = false;
  @Input() loading = false;
  /** true enquanto a sidebar/dialog está aberta — usado só pra (re)carregar ao reabrir. */
  @Input() open = false;
  /**
   * SSAPP-7: filtros já persistidos pra pré-preencher o formulário ao abrir
   * (edição de feed existente).
   */
  @Input() initialFilters?: FeedFilters | null;

  @Output() filtersChange = new EventEmitter<FeedFilterTree | undefined>();
  @Output() validityChange = new EventEmitter<boolean>();

  fieldOptions = FEED_FILTER_FIELD_OPTIONS;
  operatorOptions = FEED_FILTER_OPERATOR_OPTIONS;
  // Rótulos curtos ("E"/"OU") de propósito — viram um p-selectButton (toggle
  // sempre visível, mais ilustrativo que um dropdown de texto longo), não um
  // dropdown. O texto explicativo ("todas as regras"/"qualquer grupo") já
  // vem do label estático ao lado ("Combinar regras/grupos com:").
  ruleCombinatorOptions: { label: string; value: FeedFilterCombinator }[] = [
    { label: 'E', value: 'and' },
    { label: 'OU', value: 'or' },
  ];
  groupCombinatorOptions: { label: string; value: FeedFilterCombinator }[] = [
    { label: 'E', value: 'and' },
    { label: 'OU', value: 'or' },
  ];

  groups: FeedFilterGroup[] = [];
  topOperator: FeedFilterCombinator = 'and';

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (openChange && !openChange.firstChange && !openChange.previousValue && openChange.currentValue) {
      this.loadFilters(this.initialFilters);
    }
  }

  get isDisabled(): boolean {
    return this.disabled || this.loading;
  }

  get hasGroups(): boolean {
    return this.groups.length > 0;
  }

  addGroup(): void {
    if (this.isDisabled) return;
    this.groups = [...this.groups, createEmptyFeedFilterGroup()];
    this.emitChanges();
  }

  removeGroup(groupIndex: number): void {
    if (this.isDisabled) return;
    this.groups = this.groups.filter((_, i) => i !== groupIndex);
    this.emitChanges();
  }

  addRule(groupIndex: number): void {
    if (this.isDisabled) return;
    this.groups = this.groups.map((group, i) =>
      i === groupIndex ? { ...group, rules: [...group.rules, createEmptyFeedFilterRule()] } : group
    );
    this.emitChanges();
  }

  // Regra removida esvazia o grupo -> grupo some junto (mesma semântica de
  // antes: 0 regras não fica representado na tela, "sem filtro" é o grupo
  // ausente, não um grupo vazio esperando preenchimento).
  removeRule(groupIndex: number, ruleIndex: number): void {
    if (this.isDisabled) return;
    const group = this.groups[groupIndex];
    if (!group) return;

    const remainingRules = group.rules.filter((_, i) => i !== ruleIndex);
    this.groups = remainingRules.length === 0
      ? this.groups.filter((_, i) => i !== groupIndex)
      : this.groups.map((g, i) => (i === groupIndex ? { ...g, rules: remainingRules } : g));
    this.emitChanges();
  }

  clearFilters(): void {
    if (this.isDisabled || !this.hasGroups) return;
    this.groups = [];
    this.emitChanges();
  }

  onRuleChanged(): void {
    this.emitChanges();
  }

  /** Carrega o estado ao abrir — vazio na criação, pré-preenchido na edição (SSAPP-7). */
  private loadFilters(initial?: FeedFilters | null): void {
    this.groups = getFeedFilterGroups(initial).map(group => ({
      operator: group.operator,
      rules: group.rules.map(rule => ({ ...rule, values: [...rule.values] })),
    }));
    this.topOperator = getFeedFilterTopOperator(initial);
    this.emitChanges();
  }

  private emitChanges(): void {
    this.filtersChange.emit(serializeFeedFilters(this.groups, this.topOperator));
    this.validityChange.emit(this.groups.length === 0 || this.groups.every(isFeedFilterGroupValid));
  }
}
